/**
 * 요건확인기관 연락처 (?page=req-agencies) — 공개 도구. 문의 #25 후속(요건 Phase 2 확장).
 *   요건 법령(세관장확인/통합공고)별 소관부처 + 담당부서 직통전화 디렉토리.
 *   세번 상세 요건 팝업과 동일 소스(RequirementAgencyRef) — GET /api/onto/requirement-agencies.
 *   데이터: law.go.kr Open API 담당부서 직통전화(현행 부처명). 갱신=scripts 재실행.
 */
import React, { useEffect, useMemo, useState } from "react";
import ApiPortalLayout from "./ApiPortalLayout";
import AgencyContactList from "../audit/AgencyContactList";

const S = {
  page: { maxWidth: 920, color: "#1f2937",
    fontFamily: "'Inter','Malgun Gothic', '맑은 고딕', 'Pretendard',sans-serif", lineHeight: 1.65 },
  h1: { fontSize: 30, fontWeight: 800, marginBottom: 6 },
  p: { fontSize: 14, color: "#4b5563", margin: "6px 0" },
  input: { width: "100%", height: 44, padding: "0 12px", fontSize: 14, borderRadius: 8,
    border: "1px solid #d1d5db", outline: "none", background: "#fff",
    fontFamily: "inherit", boxSizing: "border-box" },
  chipRow: { display: "flex", flexWrap: "wrap", gap: 7, margin: "12px 0 4px" },
  chip: (on) => ({ padding: "5px 11px", borderRadius: 999, cursor: "pointer",
    border: on ? "1px solid #111827" : "1px solid #d1d5db",
    background: on ? "#111827" : "#fff", color: on ? "#fff" : "#4b5563",
    fontSize: 12.5, fontWeight: 600, fontFamily: "inherit", whiteSpace: "nowrap" }),
  tableWrap: { overflowX: "auto", marginTop: 14, border: "1px solid #eceef2",
    borderRadius: 10 },
  table: { width: "100%", borderCollapse: "collapse", minWidth: 640 },
  th: { textAlign: "left", padding: "9px 12px", whiteSpace: "nowrap", fontSize: 12,
    fontWeight: 700, color: "#374151", position: "sticky", top: 0, zIndex: 2,
    background: "#f8fafc", boxShadow: "inset 0 -2px 0 #e5e7eb", cursor: "pointer",
    userSelect: "none" },
  td: { textAlign: "left", padding: "8px 12px", borderBottom: "1px solid #f3f4f6",
    fontSize: 13, background: "#fff", verticalAlign: "middle" },
  law: { fontWeight: 600, color: "#111827" },
  tel: { fontWeight: 700, color: "#1d4ed8", textDecoration: "none",
    fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
  badge: (kind) => ({ display: "inline-block", fontSize: 10.5, fontWeight: 700,
    borderRadius: 4, padding: "1px 6px", marginRight: 4, whiteSpace: "nowrap",
    ...(kind === "세관장확인"
      ? { background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe" }
      : { background: "#ecfdf5", color: "#047857", border: "1px solid #a7f3d0" }) }),
  note: { background: "#f8fafc", borderLeft: "3px solid #94a3b8", padding: "10px 14px",
    fontSize: 12.5, color: "#475569", borderRadius: 4, margin: "14px 0 0" },
  count: { fontSize: 12.5, color: "#6b7280", marginTop: 10 },
};

function ScopeBadges({ scope }) {
  if (!scope) return <span style={{ color: "#9ca3af" }}>-</span>;
  return scope.split("·").map((s) => (
    <span key={s} style={S.badge(s)}>{s}</span>
  ));
}

// 61차: 세번 상세의 소관부처 링크(?ministry=)·검색(?q=)으로 들어오면 그 필터로 시작한다.
export function readDirectoryParams(search) {
  try {
    const p = new URLSearchParams(search != null ? search : window.location.search);
    return { ministry: (p.get("ministry") || "").trim(), q: (p.get("q") || "").trim() };
  } catch (_) {
    return { ministry: "", q: "" };
  }
}

export default function ReqAgencyDirectoryPage({ onNavigate, embedded = false }) {
  const [data, setData] = useState(null); // {footer, agencies}
  const [err, setErr] = useState(null);
  const [initial] = useState(() => readDirectoryParams());
  const [q, setQ] = useState(initial.q);
  const [ministry, setMinistry] = useState(initial.ministry); // "" = 전체
  const [sort, setSort] = useState("ministry"); // ministry | law

  useEffect(() => {
    fetch("/api/onto/requirement-agencies")
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(setData)
      .catch((e) => setErr(String(e.message || e)));
  }, []);

  const agencies = useMemo(() => data?.agencies || [], [data]);

  // 링크로 받은 부처가 목록에 없으면(표기 차이·기관명) 부처 필터 대신 검색어로 넘긴다 — 빈 화면 방지.
  useEffect(() => {
    if (!data || !ministry) return;
    if (!agencies.some((a) => a.ministry === ministry)) {
      setMinistry("");
      setQ((prev) => prev || ministry);
    }
  }, [data, ministry, agencies]);

  // 소관부처 필터 칩 (건수 desc)
  const ministries = useMemo(() => {
    const m = new Map();
    for (const a of agencies) m.set(a.ministry, (m.get(a.ministry) || 0) + 1);
    return [...m.entries()].sort((x, y) => y[1] - x[1]);
  }, [agencies]);

  const rows = useMemo(() => {
    const needle = q.toLowerCase().trim();
    let r = agencies.filter((a) => {
      if (ministry && a.ministry !== ministry) return false;
      if (!needle) return true;
      return (
        a.law.toLowerCase().includes(needle) ||
        a.ministry.toLowerCase().includes(needle) ||
        (a.tel || "").includes(needle) ||
        // 분야별 문의처의 과 이름·전화로도 찾는다("1회용품", "자원재활용과")
        (a.contacts || []).some((c) => `${c.dept} ${c.tel}`.toLowerCase().includes(needle))
      );
    });
    const col = new Intl.Collator("ko");
    r = [...r].sort((a, b) =>
      sort === "law"
        ? col.compare(a.law, b.law)
        : col.compare(a.ministry, b.ministry) || col.compare(a.law, b.law),
    );
    return r;
  }, [agencies, q, ministry, sort]);

  return (
    <ApiPortalLayout active="req-agencies" onNavigate={onNavigate} embedded={embedded}>
      <div style={S.page}>
        <h1 style={S.h1}>요건확인기관 연락처</h1>
        <p style={S.p}>
          수입·수출 요건(<b>세관장확인 · 통합공고</b>) 대상 법령별 <b>소관부처</b>와
          <b> 담당부서 직통전화</b>를 조회합니다. 세번(HS) 상세의 요건 팝업과 같은 자료입니다.
        </p>

        {err && (
          <div style={{ ...S.note, borderLeftColor: "#ef4444", color: "#b91c1c" }}>
            불러오기 실패: {err}
          </div>
        )}

        <div style={{ marginTop: 14 }}>
          <input
            style={S.input}
            placeholder="법령명 · 소관부처 · 전화 검색"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        <div style={S.chipRow}>
          <button style={S.chip(ministry === "")} onClick={() => setMinistry("")}>
            전체 {agencies.length}
          </button>
          {ministries.map(([m, n]) => (
            <button key={m} style={S.chip(ministry === m)} onClick={() => setMinistry(m)}>
              {m} {n}
            </button>
          ))}
        </div>

        {data && (
          <div style={S.count}>{rows.length}건</div>
        )}

        <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th} onClick={() => setSort("law")}>
                  법령 {sort === "law" ? "▾" : ""}
                </th>
                <th style={{ ...S.th, cursor: "default" }}>구분</th>
                <th style={S.th} onClick={() => setSort("ministry")}>
                  소관부처 {sort === "ministry" ? "▾" : ""}
                </th>
                <th style={{ ...S.th, cursor: "default" }}>담당부서 직통전화</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a, i) => (
                <tr key={a.law + i}>
                  <td style={{ ...S.td, ...S.law }}>{a.law}</td>
                  <td style={S.td}><ScopeBadges scope={a.scope} /></td>
                  <td style={S.td}>{a.ministry}</td>
                  <td style={S.td}>
                    {a.tel ? (
                      // "044-201-7382, 7391" 은 첫 번호만 건다(둘을 이어 붙이면 없는 번호가 된다).
                      <a href={`tel:${String(a.tel).split(/[,，]/)[0].replace(/[^0-9]/g, "")}`} style={S.tel}>
                        {a.tel}
                      </a>
                    ) : (
                      <span style={{ color: "#9ca3af" }}>-</span>
                    )}
                    <AgencyContactList contacts={a.contacts} compact />
                  </td>
                </tr>
              ))}
              {data && rows.length === 0 && (
                <tr>
                  <td style={{ ...S.td, color: "#9ca3af" }} colSpan={4}>
                    검색 결과가 없습니다.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={S.note}>
          {data?.footer ||
            "요건확인기관(소관부처) 담당부서 직통번호입니다(law.go.kr 기준). 정부민원안내 국번없이 110."}
        </div>
      </div>
    </ApiPortalLayout>
  );
}
